import DataGrid from "components/data-grid/DataGrid";
import {
  setBreadCrumbs,
  setCurrent,
  setNavigationComponents,
} from "features/navigation/navigationSlice";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";
import PrimaryButton from "ui/buttons/PrimaryButton";
import { columns } from "./columns";
import { useNavigate } from "react-router-dom";
import DropDownButton from "ui/buttons/DropDownButton";
import { FaDrumSteelpan } from "react-icons/fa";
import { PencilAltIcon } from "@heroicons/react/outline";
import moment from "moment";
import Card from "ui/Card";

export default function OrderRequests() {
  let navigate = useNavigate();
  let dispatch = useDispatch();
  let location = useLocation();

  const isListView =
    location.pathname === "/order-requests" ||
    location.pathname === "/order-requests/";

  // Filter state
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [tempSelectedStatuses, setTempSelectedStatuses] = useState([]);
  const [showFilter, setShowFilter] = useState(false);

  const statusOptions = [
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "shipped", label: "Shipped" },
    { value: "cancelled", label: "Cancelled" },
    { value: "pending", label: "Pending" },
    { value: "delivered", label: "Received" },
  ];

  const toggleFilter = () => {
    if (!showFilter) {
      setTempSelectedStatuses(selectedStatuses);
    }
    setShowFilter(!showFilter);
  };

  const handleCheckboxChange = (value) => {
    setTempSelectedStatuses((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const applyFilters = () => {
    setSelectedStatuses(tempSelectedStatuses);
    setShowFilter(false);
  };

  const cancelFilter = () => {
    setShowFilter(false);
  };

  useEffect(() => {
    dispatch(
      setCurrent({
        name: "Order Requests",
      })
    );
    dispatch(
      setNavigationComponents(
        <PrimaryButton
          title="Add Order Request"
          onClick={() => navigate("/order-requests/add")}
        />
      )
    );
    dispatch(
      setBreadCrumbs([
        {
          name: "Home",
          path: "/home",
        },
        {
          name: "Order Requests",
          path: "/order-requests",
        },
      ])
    );
  }, [dispatch, navigate]);

  const cellFunction = (cell) => {
    if (cell.column.id === "referenceNumber" || cell.column.id === "orderId") {



      return (
        <div
          onClick={() => {
            navigate("/order-requests/view/" + cell.row.original.id, {
              state: cell.row.original,
            });
          }}
          className="flex flex-row items-center space-x-3 cursor-pointer"
        >
          <p className="hover:underline">{cell.value}</p>
        </div>
      );
    }
    if (cell.column.id === "createdAt") {
      return moment(cell.value).format("DD MMMM YYYY");
    }

    if (cell.column.id === "actions") {
      return (
        <DropDownButton
          items={[
            // {
            //   title: "Accept",
            //   icon: <CheckCircleIcon className="h-5 w-5" />,
            //   path: "/order-requests/accept/" + cell.row.original.id,
            //   state: cell.row.original,
            // },
            // {
            //   title: "Reject",
            //   icon: <XIcon className="h-5 w-5" />,
            //   path: "/order-requests/reject/" + cell.row.original.id,
            //   state: cell.row.original,
            // },
            {
              title: "Edit",
              icon: <PencilAltIcon className="h-5 w-5" />,
              path: "/order-requests/edit/" + cell.row.original.id,
              state: cell.row.original,
            },
            {
              title: "Delete",
              icon: <FaDrumSteelpan className="h-5 w-5" />,
              path: "/order-requests/delete/" + cell.row.original.id,
              state: cell.row.original,
            },
          ]}
          title="Actions"
          icon={<i className="fas fa-ellipsis-v"></i>}
          className=""
          setSelected={(item) => {
            console.log(item);
          }}
          path="/sales/"
        />
      );
    }
    if (cell.column.id === "status") {
      //  Explicitly handle "pending" – though default case already does
      switch (cell.value) {
        case "shipped":
          return (
            <span className="px-2 inline-flex text-[10px] leading-5 rounded-full bg-fuchsia-100 text-fuchsia-800">
              Shipped
            </span>
          );
        case "delivered":
          return (
            <span className="px-2 inline-flex text-[10px] leading-5 rounded-full bg-blue-100 text-blue-800">
              Received
            </span>
          );
        case "rejected":
          return (
            <span className="px-2 inline-flex text-[10px] leading-5 rounded-full bg-red-100 text-red-800">
              Rejected
            </span>
          );
        case "approved":
          return (
            <span className="px-2 inline-flex text-[10px] leading-5 rounded-full bg-teal-100 text-teal-800">
              Approved
            </span>
          );
        case "cancelled":
          return (
            <span className="px-2 inline-flex text-[10px] leading-5 rounded-full bg-gray-100 text-gray-800">
              Cancelled
            </span>
          );
        case "pending":   //  explicit case (optional, but clear)
          return (
            <span className="px-2 inline-flex text-[10px] leading-5 rounded-full bg-amber-100 text-amber-800">
              Pending
            </span>
          );
        default:
          // fallback for any unexpected value
          return (
            <span className="px-2 inline-flex text-[10px] leading-5 rounded-full bg-gray-100 text-gray-800">
              {cell.value}
            </span>
          );
      }
    }
    return cell.value;
  };

  // Filter params sent to API
  const filterParams =
    selectedStatuses.length > 0
      ? { status: selectedStatuses.join(",") }
      : undefined;

  return (
    <div className="relative">
      {isListView && (
        <Card
          heading="Order Requests"
          description="Find all the order requests here"
        >
          <div className="relative w-full">
            {/* DataGrid with Filter Button positioned overlay */}
            <div className="relative">
              {/* Filter Button */}
              <div className="absolute top-0 right-0 z-10">
                <div className="relative">
                  <button
                    onClick={toggleFilter}
                    className="w-auto h-8 px-4 py-2 border rounded flex items-center space-x-2 text-sm bg-[#dce3ec] hover:bg-[#cbd5e1] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors"
                  >
                    <span className="hidden sm:inline">Filter</span>
                    <span className="sm:hidden">
                      <i className="fas fa-filter"></i>
                    </span>
                    <i className="fas fa-filter hidden sm:inline"></i>
                    {selectedStatuses.length > 0 && (
                      <span className="ml-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                        {selectedStatuses.length}
                      </span>
                    )}
                  </button>

                  {/* Filter Dropdown - Responsive width and positioning */}
                  {showFilter && (
                    <div className="absolute right-0 mt-2 w-screen sm:w-64 sm:max-w-xs -left-4 sm:left-auto bg-white border rounded shadow-lg z-20 p-4">
                      <p className="font-medium mb-2 text-gray-700 text-sm md:text-base">
                        Filter by Status
                      </p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {statusOptions.map((option) => (
                          <label
                            key={option.value}
                            className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={tempSelectedStatuses.includes(option.value)}
                              onChange={() => handleCheckboxChange(option.value)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-700">
                              {option.label}
                            </span>
                          </label>
                        ))}
                      </div>
                      <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4 pt-2 border-t border-gray-100">
                        <button
                          onClick={cancelFilter}
                          className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors order-2 sm:order-1"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={applyFilters}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors order-1 sm:order-2"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* DataGrid - Responsive height */}
              <DataGrid
                search={true}
                x_columns={columns}
                searchable={true}
                api={"/admin/order-requests"}
                showHeader={true}
                height={"h-[calc(100vh-230px)]"}
                cellFunction={cellFunction}
                externalParams={{ viewAll: true, ...filterParams }}
                defaultPageSize={10}
                paginationMode="server"
              />
            </div>
          </div>
        </Card>
      )}
      <Outlet />
    </div>
  );
}